import React, { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, Heart, ShoppingCart, Users, TrendingUp, Layers, Sparkles } from 'lucide-react';
import type { Recommendation } from '../types/index';
import { UserGame, RecommendationAlgorithm } from '../types/index';

// Props para o componente AlgorithmBadge
interface AlgorithmBadgeProps {
  algorithm: RecommendationAlgorithm;
}

// Props para o componente RecommendationCard
interface RecommendationCardProps {
  recommendation: Recommendation;
  userGame: UserGame;
  isPopular?: boolean;
  onFeedback: (recommendationId: string, liked: boolean, isPopular: boolean) => Promise<void>;
  onAddToCollection: (gameId: string) => Promise<void>;
  onToggleWishlist: (gameId: string) => Promise<void>;
}

// Componente Badge para o algoritmo de recomendação
const AlgorithmBadge: React.FC<AlgorithmBadgeProps> = memo(({ algorithm }) => {
  if (!algorithm) return null;
  
  let icon, label, color;
  
  switch (algorithm) {
    case 'collaborative':
      icon = <Users className="h-4 w-4" />;
      label = 'Colaborativo';
      color = 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-200';
      break;
    case 'content_based':
      icon = <Layers className="h-4 w-4" />;
      label = 'Baseado em Conteúdo';
      color = 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-200';
      break;
    case 'popular':
      icon = <TrendingUp className="h-4 w-4" />;
      label = 'Populares';
      color = 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-200';
      break;
    case 'ml':
      icon = <Sparkles className="h-4 w-4" />;
      label = 'IA';
      color = 'bg-amber-100 text-amber-800 dark:bg-amber-600 dark:text-amber-100';
      break;
    default:
      return null;
  }
  
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${color}`}>
      {icon}
      <span>{label}</span>
    </div>
  );
});

AlgorithmBadge.displayName = 'AlgorithmBadge';

// Componente principal de cartão de recomendação
const RecommendationCard: React.FC<RecommendationCardProps> = memo(({
  recommendation,
  isPopular = false,
  onFeedback,
  onAddToCollection,
  userGame = { owned: false, wishlist: false },
  onToggleWishlist
}) => {
  // Verificar se a recomendação tem jogo associado
  if (!recommendation.game) {
    console.error(`Recomendação ${recommendation.id} não tem jogo associado:`, recommendation);
    return null; // Não renderizar recomendações sem jogo
  }
  
  const game = recommendation.game;
  const isOwned = userGame.owned || false;
  const isWishlist = userGame.wishlist || false;
  
  const [isProcessing, setIsProcessing] = useState<'upvote' | 'downvote' | null>(null);

  // Funções de feedback com melhor tratamento de estado e logging
  const handleUpvote = async () => {
    if (!game) return;
    
    try {
      console.log(`RecommendationCard: Clique em upvote para "${game.name}", estado atual: ${
        recommendation.liked === null 
          ? 'Sem feedback' 
          : recommendation.liked === true 
            ? 'Upvote ativo' 
            : 'Downvote ativo'
      }`);
      
      setIsProcessing('upvote');
      await onFeedback(recommendation.id, true, isPopular);
    } catch (error) {
      console.error('Erro ao processar upvote:', error);
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDownvote = async () => {
    if (!game) return;
    
    try {
      console.log(`RecommendationCard: Clique em downvote para "${game.name}", estado atual: ${
        recommendation.liked === null 
          ? 'Sem feedback' 
          : recommendation.liked === true 
            ? 'Upvote ativo' 
            : 'Downvote ativo'
      }`);
      
      setIsProcessing('downvote');
      await onFeedback(recommendation.id, false, isPopular);
    } catch (error) {
      console.error('Erro ao processar downvote:', error);
    } finally {
      setIsProcessing(null);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full dark:bg-gray-800 dark:border-gray-700 dark:hover:shadow-gray-700/50">
      <div className="flex justify-between items-center px-4 py-2 dark:bg-gray-750 dark:border-b dark:border-gray-700">
        <AlgorithmBadge algorithm={recommendation.algorithm || RecommendationAlgorithm.POPULAR} />
        <div className="flex gap-2">
          <button
            onClick={handleUpvote}
            className={`p-1 rounded transition-colors ${
              recommendation.liked === true 
                ? 'bg-green-100 text-green-700 ring-1 ring-green-700 dark:bg-green-700/30 dark:text-green-300 dark:ring-green-300' 
                : 'text-gray-400 hover:text-green-500 hover:bg-green-50 dark:text-gray-500 dark:hover:text-green-400 dark:hover:bg-green-700/20'
            }`}
            title={recommendation.liked === true ? "Remover curtida" : "Gostei desta recomendação"}
            aria-label={recommendation.liked === true ? "Remover curtida" : "Gostei desta recomendação"}
            disabled={isProcessing !== null}
          >
            <ThumbsUp className={`h-5 w-5 ${isProcessing === 'upvote' ? 'opacity-50' : ''}`} />
          </button>
          <button
            onClick={handleDownvote}
            className={`p-1 rounded transition-colors ${
              recommendation.liked === false 
                ? 'bg-red-100 text-red-700 ring-1 ring-red-700 dark:bg-red-700/30 dark:text-red-300 dark:ring-red-300' 
                : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-700/20'
            }`}
            title={recommendation.liked === false ? "Remover descurtida" : "Não gostei desta recomendação"}
            aria-label={recommendation.liked === false ? "Remover descurtida" : "Não gostei desta recomendação"}
            disabled={isProcessing !== null}
          >
            <ThumbsDown className={`h-5 w-5 ${isProcessing === 'downvote' ? 'opacity-50' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Imagem do jogo */}
      <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
        <Link to={game.slug ? `/games/${game.slug}` : `/games/${game.id}`} className="block">
          <img
            src={game.image_url || '/images/no-image.png'}
            alt={game.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              // Usar uma imagem local como fallback em vez de placeholder.com
              e.currentTarget.src = '/images/image-not-available.png';
              // Caso a imagem local também falhe, usar um emoji ou texto
              e.currentTarget.onerror = () => {
                e.currentTarget.onerror = null;
                e.currentTarget.alt = "Imagem não disponível";
                e.currentTarget.style.display = "flex";
                e.currentTarget.style.alignItems = "center";
                e.currentTarget.style.justifyContent = "center";
                e.currentTarget.style.backgroundColor = "#f3f4f6";
                // Verificar se parentElement existe
                if (e.currentTarget.parentElement) {
                  e.currentTarget.parentElement.innerHTML = `
                    <div class="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
                      <span class="text-4xl">🎲</span>
                    </div>
                  `;
                }
              };
            }}
          />
        </Link>
      </div>
      
      {/* Informações do jogo */}
      <div className="p-4">
        <Link to={game.slug ? `/games/${game.slug}` : `/games/${game.id}`} className="block">
          <h3 className="text-lg font-semibold text-gray-800 hover:text-indigo-600 mb-1 line-clamp-1 dark:text-gray-100 dark:hover:text-indigo-400">{game.name}</h3>
        </Link>
        <div className="text-sm text-gray-500 mb-2 dark:text-gray-400">
          {game.year && <span className="mr-2">{game.year}</span>}
          {game.min_players && game.max_players && (
            <span className="mr-2">
              {game.min_players === game.max_players
                ? `${game.min_players} jogadores`
                : `${game.min_players}-${game.max_players} jogadores`}
            </span>
          )}
        </div>
        
        {/* Razão da recomendação */}
        <p className="text-sm text-gray-600 italic mb-4 line-clamp-2 dark:text-gray-300">
          {recommendation.reason || "Recomendado com base nos seus gostos"}
        </p>
        
        {/* Ações */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onAddToCollection(game.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
              isOwned 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-700/30 dark:text-blue-300' 
                : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-blue-700/20 dark:hover:text-blue-300'
            }`}
          >
            <ShoppingCart className="h-3 w-3" />
            {isOwned ? 'Na Coleção' : 'Adicionar à Coleção'}
          </button>
          
          <button
            onClick={() => onToggleWishlist(game.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs ${
              isWishlist 
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-700/30 dark:text-purple-300' 
                : 'bg-gray-100 text-gray-700 hover:bg-purple-50 hover:text-purple-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-purple-700/20 dark:hover:text-purple-300'
            }`}
          >
            <Heart className="h-3 w-3" />
            {isWishlist ? 'Na Wishlist' : 'Adicionar à Wishlist'}
          </button>
        </div>
      </div>
    </div>
  );
});

RecommendationCard.displayName = 'RecommendationCard';

export { RecommendationCard, AlgorithmBadge }; 